<template>
    <main
        v-if="data"
        class="relative overflow-hidden pt-3 md:pt-10">
        <MainSection />

        <OurProductsSection class="py-10 md:py-20" />
        <AtomHr />

        <LazyApiSection
            class="max-lg:hidden"
            min-height="24rem"
            @visible="loadDeferredHomeContent">
            <template v-if="maps && maps.data.length">
                <DeferredMapSection
                    :region-list="maps.data"
                    class="py-10 md:py-20" />
                <AtomHr />
            </template>
        </LazyApiSection>

        <template v-if="data.data.experience.data.length">
            <InNumbers
                :items="data.data.experience.data"
                class="py-10 md:py-20" />
            <AtomHr />
        </template>

        <GallerySection class="py-10 md:py-20" />
        <AtomHr />

        <LazyApiSection
            min-height="24rem"
            @visible="loadDeferredHomeContent">
            <template v-if="deferredHome?.portfolios?.data.length">
                <DeferredPortfolioSection
                    title="Интересные проекты с нашим оборудованием"
                    :items="deferredHome.portfolios.data"
                    class="py-10 md:py-20" />
                <AtomHr />
            </template>
        </LazyApiSection>

        <OurCustomers class="py-10 md:py-20" />
        <AtomHr />

        <LazyApiSection
            min-height="20rem"
            @visible="loadDeferredHomeContent">
            <template v-if="deferredHome?.employees?.data.length">
                <DeferredOurTeam
                    :items="deferredHome.employees.data"
                    class="py-10 md:py-20" />
                <AtomHr />
            </template>
        </LazyApiSection>

        <OurAdvantages class="py-10 md:py-20" />
        <AtomHr />

        <LazyApiSection
            min-height="20rem"
            @visible="loadDeferredHomeContent">
            <template v-if="deferredHome?.articles?.data.length">
                <DeferredNewsSection
                    :items="deferredHome.articles.data"
                    class="py-10 md:py-20" />
                <AtomHr />
            </template>
        </LazyApiSection>

        <CoffeeBeanSection class="py-10 md:py-20" />
    </main>
</template>

<script setup lang="ts">
import type { MainPage } from '~/types/pages.types'

import AtomHr from '~/components/atom/atom-hr/AtomHr.vue'
import CoffeeBeanSection from '~/components/pages/home/coffee-bean-section/CoffeeBeanSection.vue'
import GallerySection from '~/components/pages/home/gallery-section/GallerySection.vue'
import MainSection from '~/components/pages/home/main-section/MainSection.vue'
import OurProductsSection from '~/components/pages/home/our-products/OurProductsSection.vue'
import InNumbers from '~/components/particles/in-numbers/InNumbers.vue'
import LazyApiSection from '~/components/particles/lazy-api-section/LazyApiSection.vue'
import type { MapRegion } from '~/components/particles/map-section/map-section.types'
import OurAdvantages from '~/components/particles/our-advantages/OurAdvantages.vue'
import OurCustomers from '~/components/particles/our-customers/OurCustomers.vue'

const DeferredMapSection = defineAsyncComponent(
    () => import('~/components/particles/map-section/MapSection.vue')
)
const DeferredPortfolioSection = defineAsyncComponent(
    () =>
        import('~/components/particles/portfolio-section/PortfolioSection.vue')
)
const DeferredOurTeam = defineAsyncComponent(
    () => import('~/components/particles/our-team/OurTeam.vue')
)
const DeferredNewsSection = defineAsyncComponent(
    () => import('~/components/particles/news-section/NewsSection.vue')
)

const nuxtApp = useNuxtApp()
const modalStore = useModalStore()

const { data, error } = await useApiFetch<Page<MainPage>>('/v1/main-page', {
    key: 'main-page',
    query: {
        include: ['experience', 'schema_org'].join(','),
    },
    getCachedData(key) {
        return nuxtApp.payload?.data?.[key]
    },
    useLastModified: true,
})

if (error.value) {
    throw createError(
        Object.assign(error.value, {
            message: error.value.statusMessage,
            statusMessage: 'error',
        })
    )
}

const { data: deferredHomeData, execute: executeDeferredHomeContent } =
    await useApiFetch<Page<MainPage>>('/v1/main-page', {
        key: 'deferred-main-page',
        query: {
            include: [
                'portfolios.city',
                'portfolios.categories',
                'portfolios.products',
                'employees',
                'articles',
            ].join(','),
        },
        server: false,
        immediate: false,
        watch: false,
        getCachedData(key) {
            return nuxtApp.payload?.data?.[key]
        },
    })

const { data: maps, execute: executeMaps } = await useApiFetch<
    Page<MapRegion[]>
>('/v1/maps', {
    key: 'deferred-maps',
    server: false,
    immediate: false,
    watch: false,
    getCachedData(key) {
        return nuxtApp.payload?.data?.[key]
    },
})

const isDeferredHomeContentLoading = ref(false)
const deferredHome = computed(() => deferredHomeData.value?.data)

async function loadDeferredHomeContent() {
    if (isDeferredHomeContentLoading.value || deferredHomeData.value) {
        return
    }

    isDeferredHomeContentLoading.value = true

    await Promise.all([executeDeferredHomeContent(), executeMaps()])

    isDeferredHomeContentLoading.value = false
}

if (data.value?.data) {
    modalStore.setFeedbackLabel(data.value.data?.feedback_label || '')
} else {
    modalStore.setFeedbackLabel('')
}

if (data.value) {
    useSeoMeta({
        title: data.value.data.seo.seo_title,
        description: data.value.data.seo.seo_description,
        ogTitle: data.value.data.seo.og_title,
        ogDescription: data.value.data.seo.og_description,
        ogImage: data.value.data.seo.og_image
            ? data.value.data.seo.og_image
            : null,
    })
    useSchemaOrg([defineWebPage(), defineWebSite()])
}
</script>
